import { z } from 'zod'
import dayjs from 'dayjs'

import {
  PARCEL_SERVICE_TYPES,
  PROMOTION_REMARK_MAX_LENGTH,
  PROMOTION_TARGET_UNITS,
  PROMOTION_TITLE_MAX_LENGTH,
  PromotionBulkKind,
  PromotionTargetUnit,
  ServiceTypeCode,
} from '@/services/settlement/settlementPromotion/types'

// 구간 상한 — 등록 화면과 일괄 등록 엑셀 양식이 같은 값을 쓴다 (LOGISPRD-11434 — 5 에서 10 으로)
export const MAX_TIERS = 10
// 구간 하한 — 기간 프로모션은 구간이 없으면 할인값이 없다. 등록 화면 삭제 버튼도 같은 값을 본다 (LOGISPRD-11444)
export const MIN_TIERS = 1

// 기간 프로모션 시작일 하한 — 라인에 카테고리가 적재되기 시작한 날. 그 이전 구간은 매칭할 값이 없다
export const PERIOD_START_MIN_DATE = '2026-07-12'

// 월정산 마감일 — 26일이고 12월만 다음 해 1월 1일이다 (LOGISPRD-11504 — 서버 MonthlyClosing.closingDateOf 와 맞춘다).
// dayjs month() 는 0-index 라 11 이 12월이다. 12월은 마감일이 그달 안에 없어 12/31 까지 정산월이 열려 있다.
const closingDateOf = (ref: dayjs.Dayjs) => (ref.month() === 11 ? ref.add(1, 'month').startOf('month') : ref.date(26))

// 종료일 하한 — 아직 마감되지 않은 가장 이른 정산월의 시작일.
// 정산월은 전월 26일 ~ 당월 25일이고, 12월만 11/26 ~ 12/31 이라 그 다음은 1월 1일에 시작한다.
// 경계는 마감 시각(10:00)이 아니라 마감 날짜다 — 서버가 날짜만 비교해(MonthlyClosing.openSettlementMonth)
// 마감일 0시부터 다음 정산월이 열린다. 시각으로 재면 매달 26일 오전 10시간 동안 화면이 통과시킨
// 종료일을 서버가 PROMOTION_PERIOD_CLOSED 로 거부한다 (LOGISPRD-11444)
export const settleMonthStart = () => {
  const now = dayjs()

  // 마감일 전 — 지금 열려 있는 정산월의 시작일 (12월은 마감일이 1/1 이라 12/31 까지 이 분기다)
  if (now.isBefore(closingDateOf(now), 'day')) {
    const base = now.month() === 0 ? now.startOf('month') : now.subtract(1, 'month').date(26)
    return base.format('YYYY-MM-DD')
  }
  // 마감일 당일부터 — 다음 정산월의 시작일 (12월은 위 분기만 타므로 여기서 연말 보정이 필요 없다)
  const base = now.date(26)
  return base.format('YYYY-MM-DD')
}

/**
 * 프로모션 종류 2종 (기획 정책서 v3 · 시안 v4) — 종류가 적용범위 입력을 가른다.
 * 서버는 종류를 따로 받지 않고 축 조합으로 적용 단위(target_unit)를 판정하므로 전송하지 않는다.
 * 화면 표기 기준 순위는 일반 → 전체다. BE 선정 순위(LLD-LP10200 §4.2)는 바꾸지 않았고,
 * 상품 단위 잔존분이 있으면 여전히 1순위로 이긴다
 *
 * 상품 종류는 뺐다 (LOGISPRD-11338) — 상품 축은 바코드 기반이라 서버가 등록을 거부한다(LOGISPRD-11337).
 * SKU 기반 상품 종류는 등록 화면에서만 FF 로 연다 (아래 PRODUCT_KIND · LOGISPRD-13627).
 */
export const PROMOTION_KINDS = {
  NORMAL: { label: '일반', fields: { partner: true, brand: true, category: true } },
  ALL: { label: '전체', fields: { partner: false, brand: false, category: false } },
} as const

export type PromotionKind = keyof typeof PROMOTION_KINDS

/**
 * 상품 종류 (LOGISPRD-13627) — SKU ID 로 대상을 지정한다. 등록 화면에서 FF 가 켜졌을 때만 고를 수 있다.
 * 검색 필터·일괄 등록은 PROMOTION_KINDS 2종 그대로라 여기 따로 둔다 (상품 단위 검색은 일반에 포함된다).
 * 업체는 입력하지 않는다 — SKU ID 는 업체 사이에서 겹치지 않아 업체 전체로 저장한다 (LOGISPRD-14466)
 */
export const PRODUCT_KIND = { label: '상품', fields: { partner: false, brand: false, category: false } } as const

export type PromotionFormKind = PromotionKind | 'PRODUCT'

export const kindFieldsOf = (kind: PromotionFormKind | '') =>
  !kind ? { partner: false, brand: false, category: false } : kind === 'PRODUCT' ? PRODUCT_KIND.fields : PROMOTION_KINDS[kind].fields

// SKU ID 는 14자 고정, 한 건에 500개까지 (서버 PromotionSaveRequestDto.skuIds 계약)
export const SKU_ID_LENGTH = 14
export const MAX_SKU_IDS = 500

// 쉼표·공백·줄바꿈으로 나눠 붙여넣은 SKU ID 를 목록으로 바꾼다. 같은 값은 한 번만 남긴다
export const parseSkuIds = (raw: string): string[] => [...new Set(raw.split(/[\s,]+/).filter(Boolean))]

/**
 * 검색용 종류 → 적용 단위 목록 (LOGISPRD-11309).
 * 저장은 적용 단위 9값이고 종류는 2종이라, 종류 하나가 여러 값에 대응한다 (LLD-LP10200 §4.1.1 priority 표).
 */
export const PROMOTION_KIND_TARGET_UNITS: Record<PromotionKind, PromotionTargetUnit[]> = {
  // 일반 = 전체를 뺀 나머지. 적용 단위가 늘면 자동으로 따라온다.
  // 상품 종류가 빠져(LOGISPRD-11338) 기존 상품 단위 등록분도 여기로 걸린다
  NORMAL: PROMOTION_TARGET_UNITS.filter((unit) => unit !== 'ALL'),
  ALL: ['ALL'],
}

/**
 * 일괄 등록 검증 표의 '프로모션 종류' 열 표기 — 서버가 양식에서 읽은 값을 그대로 준다.
 * 적용범위 축에서 되읽으면 비워 둔 칸과 전체 적용을 가를 수 없어 미입력 행까지 '전체' 로 보였다 (LOGISPRD-11492).
 * 읽지 못한 칸, 그리고 BE 가 목록에 없는 값을 준 칸은 국가·주문유형 열과 같은 규칙으로 '-' 로 둔다.
 */
export const toPromotionKindLabel = (kind?: PromotionBulkKind | null): string =>
  kind === 'PRODUCT' ? PRODUCT_KIND.label : kind ? (PROMOTION_KINDS[kind as keyof typeof PROMOTION_KINDS]?.label ?? '-') : '-'

/**
 * 일괄 등록 검증 표의 업체·브랜드·카테고리 열 표기.
 * 비워 둔 칸을 '전체' 로 찍으면 입력 누락과 전체 적용을 가를 수 없어, 국가·주문유형·종류 열과 같은 규칙으로 '-' 로 둔다 (LOGISPRD-11492).
 */
export const joinOrDash = (values?: string[]): string => (values && values.length > 0 ? values.join(', ') : '-')

// 저장된 축에서 종류를 되읽는다 (수정 모드 프리필·목록 표시)
export const toPromotionKind = (form: {
  partnerIds?: string
  brandIds?: string[]
  categoryCodes?: string[]
}): PromotionKind | '' => {
  if (form.partnerIds || form.brandIds?.length || form.categoryCodes?.length) return 'NORMAL'
  return 'ALL'
}

// 목록의 '프로모션 종류' 열. 상품은 축만 보면 일반·전체와 구분되지 않으므로 서버가 판정한 적용 단위로 가른다 (LOGISPRD-13627)
export const toPromotionKindListLabel = (row: {
  targetUnit?: PromotionTargetUnit
  partnerIds?: string
  brandIds?: string[]
  categoryCodes?: string[]
}): string => (row.targetUnit === 'PRODUCT' ? PRODUCT_KIND.label : PROMOTION_KINDS[toPromotionKind(row) || 'ALL'].label)


// 정액 할인은 원 단위 정수만 받는다 (LOGISPRD-11314). 자리수는 DB 컬럼 한계까지 —
// 정수부 16자리 (BE Promotion.kt discount_value DECIMAL(18,2), LOGISPRD-11361).
// 이 상한은 Number.MAX_SAFE_INTEGER(9007199254740991) 를 넘어, 값을 숫자로 바꾸는 순간 반올림된다.
// 그래서 폼·표시·전송·API 타입 전 구간에서 문자열로 다루고, 판정도 자릿수로 한다
export const FIXED_MAX_DIGITS = 16
// 정률 할인율은 소수점 둘째 자리까지 (같은 컬럼의 scale)
export const RATE_MAX_DECIMALS = 2

// 입력 단계에서 자른다 — 정률은 소수점 셋째 자리부터, 정액은 정수 17자리째부터. 정액의 소수부는 통째로 버린다.
// 아래 검증에 걸어 두는 것만으로는 안내문구가 뜨지 않는다: superRefine 은 필수값을 다 채워야 돌고,
// react-hook-form 의 onChange 모드는 방금 바뀐 필드의 에러만 화면에 반영한다 (LOGISPRD-11342)
export const limitDiscountInput = (raw: string, isRate: boolean) => {
  const [whole, ...decimals] = raw.replace(/[^0-9.]/g, '').split('.')
  // 정률은 100 이하라 정수부를 자를 일이 없다 — 범위 판정은 아래 discountValueRule 이 한다.
  // 정액은 앞자리 0 을 먼저 지운다 — 검증이 유효숫자로 세므로, 안 지우면 '01234...' 붙여넣기에서 끝자리가 조용히 잘린다
  const head = isRate ? whole : whole.replace(/^0+(?=\d)/, '').slice(0, FIXED_MAX_DIGITS)
  // 정액은 소수점을 받지 않는다 — 찍어도 정수부만 남는다
  if (!isRate) return head
  // 소수점이 둘 이상이면 첫 번째만 남긴다 ('1.1.2' → '1.12')
  return decimals.length ? `${head}.${decimals.join('').slice(0, RATE_MAX_DECIMALS)}` : head
}

// 정률 0% 는 할인이 아니라 등록해도 정산 금액이 그대로다. 대신 정액 0 원을 상위 프로모션에서
// 대상을 빼는 제외 수단으로 쓴다 (ADR-20260720-01 §1). 구간(tier)만 정률 0 을 받는다 —
// 누적 수량이 적은 첫 구간을 0% 로 두는 것이 대표 사용 패턴이다 (ADR Tier 정의, VDC 출고택배비).
// BE PromotionValidator.checkDiscount · PeriodPromotionValidator.requireTierInput 와 같은 범위 (LOGISPRD-11314)
const discountValueRule = (
  discountType: string,
  raw: string,
  ctx: z.RefinementCtx,
  path: (string | number)[],
  allowZeroRate = false,
) => {
  const v = Number(raw)
  if (!raw || Number.isNaN(v) || v < 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: '할인값은 0 이상이어야 합니다.' })
    return
  }
  if (discountType === 'RATE') {
    if (!allowZeroRate && v === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: '할인율은 0%보다 커야 합니다.' })
      return
    }
    if (v > 100) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: '정률 할인은 100%를 초과할 수 없습니다.' })
      return
    }
    const decimalPart = raw.split('.')[1]
    if (decimalPart && decimalPart.length > RATE_MAX_DECIMALS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: `소수점 이하 ${RATE_MAX_DECIMALS}자리까지만 입력해 주세요.` })
    }
  } else {
    // 상한(정수부 16자리)이 Number.MAX_SAFE_INTEGER(정수부 16자리, 9007199254740991)를 넘어선다.
    // 숫자로 바꿔 비교하면 그 위 구간이 반올림돼 통과·차단이 뒤집히므로 문자열 자릿수로 센다
    const [whole, decimalPart] = raw.split('.')
    if (whole.replace(/^0+/, '').length > FIXED_MAX_DIGITS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: `정액 할인은 정수 ${FIXED_MAX_DIGITS}자리까지 입력할 수 있습니다.` })
    } else if (decimalPart) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: '정액 할인은 정수만 입력 가능합니다.' })
    }
  }
}

// 시작 수량은 행 하나만 보면 판정되므로 필드 스키마에 둔다.
// 최상위 superRefine 은 국가·주문유형이 채워지기 전엔 실행되지 않고(z.enum 이 aborted 를 낸다),
// react-hook-form 의 onChange 검증은 방금 바뀐 필드의 에러만 화면에 넣는다. 여기 있어야 입력 즉시 뜬다 (LOGISPRD-11429)
// 입력 칸이 숫자만 남기므로(PromotionFormModal 의 시작 수량 onChange) 판정도 같은 모양으로 둔다.
// Number() 로 보면 '1e3' · '0x10' 이 통과한다
const isPositiveInteger = (raw: string) => /^[1-9][0-9]*$/.test(raw)

const tierSchema = z.object({
  fromQuantity: z.string().refine(isPositiveInteger, '시작 수량은 0보다 큰 정수여야 합니다.'),
  toQuantity: z.string(), // 빈 문자열 = 무한(이상)
  discountValue: z.string(),
})

// 등록 폼 스키마
export const settlementPromotionSchema = z
  .object({
    country: z.enum(['KR', 'JP'], { errorMap: () => ({ message: '국가를 선택해 주세요.' }) }),
    // LOGISPRD-11315 — 국가와 짝으로 부과 기준(anchor)을 고른다. 선택 가능한 값은 조합 조회 결과가 정한다
    orderType: z.enum(['DOMESTIC', 'GLOBAL'], { errorMap: () => ({ message: '주문유형을 선택해 주세요.' }) }),
    // 화면 전용 — 적용범위 입력을 가른다. 서버 전송 없음 (축 조합으로 판정). '' = 미선택 (시안 v4 기본값)
    promotionKind: z.enum(['', 'NORMAL', 'ALL', 'PRODUCT']),
    serviceType: z.string().min(1, '서비스 항목을 선택해 주세요.'),
    unitType: z.enum(['PCS', 'BOX']), // LP8658 — INBOUND PCS/BOX 분화 (composite 선택값에서 분해, 기본 PCS)
    title: z
      .string()
      .min(1, '프로모션명을 입력해 주세요.')
      .max(PROMOTION_TITLE_MAX_LENGTH, `프로모션명을 ${PROMOTION_TITLE_MAX_LENGTH}자 이하로 입력해 주세요.`),
    // 업체·브랜드·카테고리 각각 전체/특정 자유 조합 (LLD-LP10200 §4.1.1) — 빈 값 = 전체
    partnerIds: z.string(), // '' = 전체. MEMO: AS-IS는 string, TO-BE는 array로 변경될 수 있음. (서버는 array로 받도록 작업 되어있음.)
    brandIds: z.array(z.string()),
    // 물류카테고리 다건 — 빈 배열 = 전체 (moms 공통코드 category####)
    categoryCodes: z.array(z.string()),
    // 상품 종류의 SKU ID 입력 원문 (LOGISPRD-13627). 전송 직전에 parseSkuIds 로 목록으로 바꾼다
    skuIdsText: z.string(),
    discountType: z.enum(['RATE', 'FIXED']),
    settlementCycle: z.enum(['DAILY', 'MONTHLY']),
    discountValue: z.string(),
    tiers: z.array(tierSchema),
    startDate: z.string().min(1, '시작일을 선택해 주세요.'),
    endDate: z.string().min(1, '종료일을 선택해 주세요.'),
    applyToAllPartners: z.boolean(),
    applyToAllBrands: z.boolean(),
    // 비고 — 선택 입력. 정산 금액에도 중복 등록 판정에도 쓰이지 않는다 (PRD §4.1 15번 · §9)
    remark: z.string().max(PROMOTION_REMARK_MAX_LENGTH, `비고를 ${PROMOTION_REMARK_MAX_LENGTH}자 이하로 입력해 주세요.`),
  })
  .superRefine((form, ctx) => {
    // 종류별 적용범위 제약 (시안 v4 · 정책서 v3 §3.2)
    const isParcel = PARCEL_SERVICE_TYPES.includes(form.serviceType as ServiceTypeCode)
    if (!form.promotionKind) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['promotionKind'], message: '프로모션 종류를 선택해 주세요.' })
    }
    if (form.promotionKind === 'ALL') {
      if (form.partnerIds || form.brandIds.length || form.categoryCodes.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['promotionKind'], message: '전체 프로모션은 적용범위를 지정할 수 없습니다.' })
      }
    }
    if (form.promotionKind === 'NORMAL') {
      // 세 축을 모두 전체로 두면 전체 프로모션과 같은 대상이 된다.
      // 업체가 전체일 때 브랜드는 지정할 수 없으므로(아래 규칙) 남는 축은 카테고리뿐이다 — 안내도 카테고리 자리에 붙인다.
      // 택배비는 카테고리를 못 쓰고 업체가 필수라, 아래 택배비 규칙이 더 정확한 문구를 낸다 (LOGISPRD-11341)
      if (!isParcel && !form.partnerIds && !form.brandIds.length && !form.categoryCodes.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['categoryCodes'],
          message: '업체아이디가 전체이면 카테고리를 1개 이상 지정해야 합니다. (모두 전체로 두려면 종류를 전체로)',
        })
      }
      if (form.brandIds.length && !form.partnerIds) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['brandIds'], message: '브랜드는 업체를 선택해야 지정할 수 있습니다.' })
      }
      if (isParcel && (form.brandIds.length || form.categoryCodes.length)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['brandIds'],
          message: '택배비는 업체까지만 지정할 수 있습니다. (브랜드 · 카테고리 지정 불가)',
        })
      }
      if (isParcel && !form.partnerIds) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['partnerIds'],
          message: '택배비 일반 프로모션은 업체 지정이 필수입니다. 전체 업체에 적용하려면 종류를 전체로 등록해 주세요.',
        })
      }
    }
    if (form.promotionKind === 'PRODUCT') {
      const skuIds = parseSkuIds(form.skuIdsText)
      if (isParcel) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['promotionKind'], message: '택배비는 상품 프로모션을 등록할 수 없습니다.' })
      }
      if (!skuIds.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['skuIdsText'], message: 'SKU ID 를 1개 이상 입력해 주세요.' })
      } else if (skuIds.length > MAX_SKU_IDS) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['skuIdsText'], message: `SKU ID 는 최대 ${MAX_SKU_IDS}개까지 입력할 수 있습니다.` })
      } else {
        const invalid = skuIds.filter((skuId) => skuId.length !== SKU_ID_LENGTH)
        if (invalid.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['skuIdsText'],
            message: `SKU ID 는 ${SKU_ID_LENGTH}자입니다. (${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? ' 외' : ''})`,
          })
        }
      }
    }

    // 할인값 — 일별은 단일값, 기간은 구간(tier)별로 검증
    if (form.settlementCycle === 'DAILY') {
      discountValueRule(form.discountType, form.discountValue, ctx, ['discountValue'])
    } else {
      if (form.tiers.length < MIN_TIERS) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tiers'], message: `기간 프로모션은 구간을 ${MIN_TIERS}개 이상 등록해야 합니다.` })
      }
      // 일괄 등록 엑셀은 구간을 한 칸에 적고 BE 파서가 같은 상한(MAX_TIERS)으로 자른다
      if (form.tiers.length > MAX_TIERS) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tiers'], message: `구간은 최대 ${MAX_TIERS}개까지 등록할 수 있습니다.` })
      }
      let prevTo: number | null = 0
      form.tiers.forEach((tier, i) => {
        const from = Number(tier.fromQuantity)
        const to = tier.toQuantity === '' ? null : Number(tier.toQuantity)
        // 시작 수량 자체의 문구는 tierSchema 가 낸다. 여기서는 뒤 검사를 건너뛰기만 한다
        if (!isPositiveInteger(tier.fromQuantity)) return
        if (to !== null && (Number.isNaN(to) || !Number.isInteger(to) || to <= from)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['tiers', i, 'toQuantity'],
            message: '종료 수량은 시작 수량보다 큰 정수여야 합니다. (빈 값 = 이상)',
          })
          return
        }
        // 구간 연속·비중첩: 다음 구간 시작 = 이전 구간 종료 + 1 (LLD-LP10200 §4.4 — 등록 서비스 검증과 동일 규칙)
        if (prevTo === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['tiers', i, 'fromQuantity'],
            message: '무한(이상) 구간 뒤에는 구간을 추가할 수 없습니다.',
          })
        } else if (i > 0 && from !== prevTo + 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['tiers', i, 'fromQuantity'],
            message: `구간은 연속되어야 합니다. (이전 종료 수량 + 1 = ${prevTo + 1})`,
          })
        }
        prevTo = to
        discountValueRule(form.discountType, tier.discountValue, ctx, ['tiers', i, 'discountValue'], true)
      })
    }

    // 기간 프로모션은 시작일과 종료일의 하한이 서로 다르다.
    //  - 시작일: 카테고리 적재 시작일(2026-07-12). 과거로의 소급은 여기까지 열린다
    //  - 종료일: 아직 마감되지 않은 정산월의 시작일. 마감(26일 10:00)이 지난 정산월엔 차감할 회차가 없다
    // 일별 프로모션은 둘 다 오늘이 하한이다.
    const isMonthlyCycle = form.settlementCycle === 'MONTHLY'
    const today = dayjs().format('YYYY-MM-DD')
    const startMinDate = isMonthlyCycle ? PERIOD_START_MIN_DATE : today
    const endMinDate = isMonthlyCycle ? settleMonthStart() : today
    if (form.startDate && form.startDate < startMinDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: isMonthlyCycle
          ? `기간 프로모션의 시작일은 카테고리 적재 시작일(${startMinDate}) 이후여야 합니다.`
          : `일별 프로모션의 시작일은 오늘(${startMinDate}) 이후여야 합니다.`,
      })
    }
    if (form.endDate && form.endDate < endMinDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: isMonthlyCycle
          ? `종료일이 이미 마감된 정산월입니다. 미마감 정산월 시작일(${endMinDate}) 이후로 지정해 주세요.`
          : `일별 프로모션의 종료일은 오늘(${endMinDate}) 이후여야 합니다.`,
      })
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: '종료일은 시작일과 같거나 이후여야 합니다.' })
    }
  })

// 수정 모드 판별 — 저장된 값이 넘어오면 종류를 역산해 채운다
const isEditPrefill = (parsedQs: Partial<SettlementPromotionSchemaType>) => Boolean(parsedQs.title)

export const getInitForm = (parsedQs: Partial<SettlementPromotionSchemaType> = {}) => {
  return {
    ...parsedQs,
    unitType: parsedQs.unitType ?? ('PCS' as const),
    country: parsedQs.country,
    orderType: parsedQs.orderType,
    // 기간은 할인값 필드가 숨겨져 required(z.string()) 를 채울 수 없으므로 빈 문자열 기본값 필수 (DAILY 검증은 superRefine)
    discountValue: parsedQs.discountValue ? String(parsedQs.discountValue) : '',
    discountType: parsedQs.discountType ?? ('RATE' as const),
    settlementCycle: parsedQs.settlementCycle ?? ('DAILY' as const),
    // 수정 모드는 저장된 축에서 종류를 되읽고, 신규는 미선택으로 둔다 (시안 v4 — 기본값 없음)
    promotionKind: parsedQs.promotionKind ?? (isEditPrefill(parsedQs) ? toPromotionKind(parsedQs) : ('' as const)),
    partnerIds: parsedQs.partnerIds ?? '',
    categoryCodes: parsedQs.categoryCodes ?? [],
    brandIds: parsedQs.brandIds ?? [],
    skuIdsText: parsedQs.skuIdsText ?? '',
    tiers: parsedQs.tiers ?? [],
    remark: parsedQs.remark ?? '',
    applyToAllBrands: false,
    applyToAllPartners: false,
  }
}

export type SettlementPromotionSchemaType = z.infer<typeof settlementPromotionSchema>
